import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const RoadmapEditor = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const { data: roadmapItems, isLoading } = useQuery({
    queryKey: ['roadmap-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roadmap_items')
        .select('*')
        .order('created_at');
      
      if (error) throw error;
      return data;
    },
  });

  const addItemMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const features = String(formData.get('features'))
        .split(',')
        .map(feature => feature.trim())
        .filter(Boolean);

      const item = {
        title: String(formData.get('title')),
        status: String(formData.get('status')),
        quarter: String(formData.get('quarter')),
        features,
      };

      const { error } = await supabase
        .from('roadmap_items')
        .insert([item]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap-items'] });
      toast.success('Roadmap item added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add roadmap item: ' + error.message);
    }
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FormData }) => {
      const features = String(formData.get('features'))
        .split(',')
        .map(feature => feature.trim())
        .filter(Boolean);

      const updates = {
        title: String(formData.get('title')),
        status: String(formData.get('status')),
        quarter: String(formData.get('quarter')),
        features,
      };

      const { error } = await supabase
        .from('roadmap_items')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap-items'] });
      toast.success('Roadmap item updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update roadmap item: ' + error.message);
    }
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('roadmap_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap-items'] });
      toast.success('Roadmap item deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete roadmap item: ' + error.message);
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              try {
                await addItemMutation.mutateAsync(new FormData(e.currentTarget));
                (e.target as HTMLFormElement).reset();
              } finally {
                setIsSubmitting(false);
              }
            }} 
            className="space-y-4"
          >
            <div>
              <Label>Title</Label>
              <Input name="title" required />
            </div>

            <div>
              <Label>Status</Label>
              <Select name="status" defaultValue="planned">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="launched">Launched</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Quarter</Label>
              <Input name="quarter" placeholder="e.g. Q4 2024" required />
            </div>

            <div>
              <Label>Features (comma-separated)</Label>
              <Input name="features" placeholder="Feature 1, Feature 2, Feature 3" required />
            </div>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Roadmap Item
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {roadmapItems?.map((item) => (
          <Card key={item.id}>
            <CardContent className="pt-6">
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  setIsSubmitting(true);
                  try {
                    await updateItemMutation.mutateAsync({
                      id: item.id,
                      formData: new FormData(e.currentTarget)
                    });
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                className="space-y-4"
              >
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input name="title" defaultValue={item.title} required />
                  </div>

                  <div>
                    <Label>Status</Label>
                    <Select name="status" defaultValue={item.status}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="launched">Launched</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="planned">Planned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Quarter</Label>
                    <Input name="quarter" defaultValue={item.quarter} required />
                  </div>

                  <div>
                    <Label>Features (comma-separated)</Label>
                    <Input 
                      name="features" 
                      defaultValue={item.features.join(', ')} 
                      required 
                    />
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => deleteItemMutation.mutate(item.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};